import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Upload, Save, ImagePlus, Wand2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/config";
import { copyText } from "@/lib/clipboard";

const inputCls =
  "w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold";
const label = "text-xs tracking-[0.25em] uppercase text-gold font-medium";

const EMPTY = { title: "", excerpt: "", body: "", tips: [], tags: [], instagram: "", image_url: "", published: false };

export default function PostEditorDialog({ post, open, onClose, onSaved, headers }) {
  const [form, setForm] = useState(EMPTY);
  const [idea, setIdea] = useState("");
  const [shots, setShots] = useState([]);
  const [imgPrompt, setImgPrompt] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const coverRef = useRef(null);
  const media = (url) => (url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

  useEffect(() => {
    if (!open) return;
    setForm(post ? { ...EMPTY, ...post } : EMPTY);
    setIdea("");
    setShots([]);
    setImgPrompt("");
  }, [open, post?.id]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const uploadShots = async (files) => {
    setBusy("shots");
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/admin/posts/image`, fd, { headers });
        uploaded.push(res.data);
      }
      setShots((s) => [...s, ...uploaded].slice(0, 4));
      if (!form.image_url && uploaded[0]) set({ image_url: uploaded[0].url });
      toast.success("Fotos subidas");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron subir las fotos");
    } finally {
      setBusy("");
    }
  };

  const uploadCover = async (file) => {
    setBusy("cover");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/posts/image`, fd, { headers });
      set({ image_url: res.data.url });
      toast.success("Imagen del artículo actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setBusy("");
    }
  };

  const generate = async () => {
    setBusy("ia");
    try {
      const res = await axios.post(
        `${API}/admin/posts/generate`,
        { prompt: idea, image_paths: shots.map((s) => s.path) },
        { headers }
      );
      set(res.data);
      toast.success("Artículo generado · ahora puedes retocarlo a tu gusto");
    } catch (err) {
      toast.error(err.response?.data?.detail || "La IA no pudo generar el artículo");
    } finally {
      setBusy("");
    }
  };

  const generateImage = async () => {
    setBusy("img");
    try {
      const res = await axios.post(`${API}/admin/posts/image/ai`, { prompt: imgPrompt || form.title }, { headers });
      set({ image_url: res.data.url });
      toast.success("Imagen creada con IA");
    } catch (err) {
      toast.error(err.response?.data?.detail || "La IA no pudo crear la imagen");
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...form, tips: form.tips.filter(Boolean), tags: form.tags.filter(Boolean) };
      const res = post
        ? await axios.put(`${API}/admin/posts/${post.id}`, payload, { headers })
        : await axios.post(`${API}/admin/posts`, payload, { headers });
      toast.success(post ? "Artículo guardado" : "Artículo creado");
      onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar el artículo");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="post-editor-dialog" data-lenis-prevent className="bg-creamcard border-sand max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-obsidian">
            {post ? "Editar artículo" : "Nuevo artículo con IA"}
          </DialogTitle>
          <DialogDescription className="text-taupe">
            Cuéntale tu idea a la IA o súbele fotos de tus trabajos: te redacta el artículo y luego lo cambias todo a mano.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl border border-gold/40 bg-rosesoft/40 p-4 grid gap-3">
          <p className={label}>Generar con inteligencia artificial</p>
          <textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            rows={3}
            placeholder="Ej.: tendencia de uñas chocolate glazed para este otoño, con purpurina fina y acabado espejo"
            data-testid="post-idea-input"
            className={inputCls}
          />
          {shots.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="post-shots">
              {shots.map((s) => (
                <div key={s.path} className="relative">
                  <img src={media(s.url)} alt="" className="w-16 h-16 rounded-lg object-cover border border-sand" />
                  <button
                    onClick={() => setShots((prev) => prev.filter((p) => p.path !== s.path))}
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-obsidian text-white p-0.5"
                    aria-label="Quitar foto"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files.length && uploadShots([...e.target.files])} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy === "shots"}
              data-testid="post-upload-shots-btn"
              className="inline-flex items-center gap-2 rounded-xl border border-sand bg-cream text-cocoa text-sm px-5 py-2.5 hover:border-gold hover:text-gold transition-colors"
            >
              {busy === "shots" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir fotos
            </button>
            <button
              onClick={generate}
              disabled={busy === "ia" || (!idea.trim() && shots.length === 0)}
              data-testid="post-generate-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 transition-colors"
            >
              {busy === "ia" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Escribir el artículo
            </button>
          </div>
        </div>

        <div className="grid gap-3">
          <p className={label}>Artículo</p>
          <input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Título" data-testid="post-title-input" className={inputCls} />
          <textarea value={form.excerpt} onChange={(e) => set({ excerpt: e.target.value })} rows={2} placeholder="Entradilla breve" data-testid="post-excerpt-input" className={inputCls} />
          <textarea value={form.body} onChange={(e) => set({ body: e.target.value })} rows={10} placeholder="Cuerpo del artículo (separa los párrafos con una línea en blanco)" data-testid="post-body-input" className={inputCls} />
          <textarea
            value={form.tips.join("\n")}
            onChange={(e) => set({ tips: e.target.value.split("\n") })}
            rows={4}
            placeholder="Consejos, uno por línea"
            data-testid="post-tips-input"
            className={inputCls}
          />
          <input
            value={form.tags.join(", ")}
            onChange={(e) => set({ tags: e.target.value.split(",").map((t) => t.trim()) })}
            placeholder="Etiquetas separadas por comas"
            data-testid="post-tags-input"
            className={inputCls}
          />
          <textarea
            value={form.instagram}
            onChange={(e) => set({ instagram: e.target.value })}
            rows={3}
            placeholder="Frase para Instagram con hashtags (solo la ves tú, para copiar y pegar)"
            data-testid="post-instagram-input"
            className={inputCls}
          />
          {form.instagram && (
            <button
              onClick={async () => { await copyText(form.instagram); toast.success("Texto de Instagram copiado"); }}
              data-testid="post-copy-instagram-btn"
              className="w-fit text-xs rounded-full border border-sand px-4 py-2 text-cocoa hover:border-gold hover:text-gold transition-colors"
            >
              Copiar para Instagram
            </button>
          )}
        </div>

        <div className="grid gap-3">
          <p className={label}>Imagen del artículo</p>
          {form.image_url && (
            <img src={media(form.image_url)} alt="" data-testid="post-cover-preview" className="w-full h-48 object-cover rounded-xl border border-sand" />
          )}
          <div className="flex flex-wrap gap-2">
            <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} />
            <button
              onClick={() => coverRef.current?.click()}
              disabled={busy === "cover"}
              data-testid="post-upload-cover-btn"
              className="inline-flex items-center gap-2 rounded-xl border border-sand bg-cream text-cocoa text-sm px-5 py-2.5 hover:border-gold hover:text-gold transition-colors"
            >
              {busy === "cover" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Subir imagen
            </button>
            {form.image_url && (
              <button onClick={() => set({ image_url: "" })} data-testid="post-remove-cover-btn" className="inline-flex items-center gap-2 rounded-xl border border-sand text-taupe text-sm px-5 py-2.5 hover:text-obsidian transition-colors">
                Quitar imagen
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)} placeholder="O pide una imagen a la IA: uñas chocolate con brillo espejo" data-testid="post-image-prompt-input" className={inputCls} />
            <button
              onClick={generateImage}
              disabled={busy === "img" || (!imgPrompt.trim() && !form.title.trim())}
              data-testid="post-generate-image-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-obsidian hover:bg-cocoa disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 transition-colors"
            >
              {busy === "img" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Crear con IA
            </button>
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm text-cocoa">
          <input type="checkbox" checked={form.published} onChange={(e) => set({ published: e.target.checked })} data-testid="post-published-toggle" className="w-4 h-4 accent-[#C5A059]" />
          Publicado en la web
        </label>

        <button
          onClick={save}
          disabled={!form.title.trim() || busy === "save"}
          data-testid="post-save-btn"
          className="w-full rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium py-3 transition-colors flex items-center justify-center gap-2"
        >
          {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar artículo
        </button>
      </DialogContent>
    </Dialog>
  );
}
